import React, { useEffect, useState } from 'react';
import Row from 'src/components/Row';
import Col from 'src/components/Col';
import { serviceRequestTableLocalTime } from 'src/helpers/validationHelper';
import { toast } from 'react-toastify';
import { fetchWarrantyStatus } from 'src/services/client';
import { format } from 'date-fns';
import ModernDatePicker from 'src/components/SharedComponents/ModernDatePicker/ModernDatePicker';
import moment from 'moment';

interface KeyPickupWarrantyDetailsProps {
  suiteId: string;
  warrantyStartDate: string;
  onWarrantyStartDateChange: (date: string) => void;
}

interface WarrantyInfo {
  id: string;
  assetWarrantyDefaultStartDate: string | null;
  assetWarrantyDefaultEndDate: string | null;
}

const KeyPickupWarrantyDetails = ({
  suiteId,
  warrantyStartDate,
  onWarrantyStartDateChange,
}: KeyPickupWarrantyDetailsProps) => {
  const [warrantyInfo, setWarrantyInfo] = useState<WarrantyInfo | null>(null);

  const loadWarrantyInfo = async () => {
    try {
      const response = await fetchWarrantyStatus(suiteId);

      if (!response || !('status' in response) || response.status !== 200) {
        console.error('API returned an unsuccessful status:', response);
        toast.error('Failed to fetch warranty information');
        setWarrantyInfo(null);
        return;
      }

      const data: WarrantyInfo = await response.json();
      setWarrantyInfo(data);

      // Set initial warranty start date if not already set and we have data
      if (!warrantyStartDate && data.assetWarrantyDefaultStartDate) {
        // Keep the original ISO format
        onWarrantyStartDateChange(data.assetWarrantyDefaultStartDate);
      }
    } catch (error) {
      console.error('Error in loadWarrantyInfo:', error);
      toast.error('Failed to fetch warranty information');
      setWarrantyInfo(null);
    }
  };

  useEffect(() => {
    if (suiteId) {
      loadWarrantyInfo();
    }
  }, [suiteId]);

  // Convert string date to Date object for the date picker
  const getDateValue = (): Date | null => {
    if (!warrantyStartDate) return null;
    try {
      return new Date(warrantyStartDate);
    } catch (error) {
      console.error('Error parsing warranty start date:', error);
      return null;
    }
  };

  // Handle date change from the date picker
  const handleDateChange = (date: Date | null) => {
    if (date) {
      onWarrantyStartDateChange(date.toISOString());
    } else {
      onWarrantyStartDateChange('');
    }
  };

  return (
    <div style={{ marginBottom: '1.5rem' }}>
      <Row>
        <Col xs="12" md="6">
          <ModernDatePicker
            label="Warranty Start Date"
            required={true}
            value={getDateValue()}
            onChange={handleDateChange}
            placeholder="Select warranty start date"
            fullWidth={true}
          />
        </Col>
        {warrantyInfo?.assetWarrantyDefaultEndDate && (
          <Col xs="12" md="6">
            <div style={{ marginTop: '1.5rem' }}>
              <label
                style={{
                  fontWeight: 'bold',
                  marginBottom: '0.5rem',
                  display: 'block',
                }}
              >
                Default Warranty End Date
              </label>
              <p style={{ margin: 0, color: '#666' }}>
                {(() => {
                  try {
                    const localTimeString = serviceRequestTableLocalTime(
                      warrantyInfo.assetWarrantyDefaultEndDate,
                      'suiteform'
                    );
                    const date = new Date(localTimeString);
                    return format(date, 'MM/dd/yyyy');
                  } catch (error) {
                    console.error('Error formatting end date:', error);
                    return 'Invalid date';
                  }
                })()}
              </p>
            </div>
          </Col>
        )}
      </Row>
    </div>
  );
};

export default KeyPickupWarrantyDetails;
